import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Compass, MagnifyingGlass, ListBullets, Share, ArrowRight } from "@phosphor-icons/react";

export default function Discover() {
  const [playlists, setPlaylists] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);

  const load = (q = "") => {
    setLoading(true);
    api.get("/discover/playlists", { params: { q, limit: 40 } })
      .then(r => setPlaylists(r.data || []))
      .catch(() => setPlaylists([]))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8 sm:py-12 pb-32 sm:pb-40">
      <div className="mb-6 sm:mb-10 border-b border-[#1F2924]/20 pb-4 sm:pb-6">
        <div className="font-mono text-[10px] sm:text-xs uppercase tracking-[0.3em] text-[#D35230] mb-2 flex items-center gap-2">
          <Compass size={14} weight="fill" /> Public feed
        </div>
        <h1 className="font-serif-display text-3xl sm:text-5xl lg:text-6xl leading-tight">Discover playlists</h1>
        <p className="text-sm sm:text-base text-[#1F2924]/70 mt-2 sm:mt-3 max-w-2xl">
          A worldly feed of music video playlists shared by other Recho Music listeners.
        </p>
      </div>

      <form
        onSubmit={(e) => { e.preventDefault(); load(search); }}
        className="flex items-center gap-2 mb-6"
      >
        <div className="flex-1 flex items-center gap-2 px-3 py-2.5 bg-[#EAE4D3] border-2 border-[#1F2924]/30 focus-within:border-[#D35230]">
          <MagnifyingGlass size={16} color="#4A5A50" />
          <input
            data-testid="discover-search-input"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search public playlists…"
            className="flex-1 bg-transparent outline-none font-sans text-sm sm:text-base"
          />
        </div>
        <button
          data-testid="discover-search-button"
          type="submit"
          className="px-4 sm:px-5 py-2.5 border-2 border-[#1F2924] bg-[#1F2924] text-[#F4F1EA] hover:bg-[#D35230] hover:border-[#D35230] font-mono text-[10px] sm:text-xs uppercase tracking-[0.2em]"
        >
          Search
        </button>
      </form>

      {loading ? (
        <div className="font-mono text-xs uppercase tracking-[0.2em] text-[#D35230]">Loading…</div>
      ) : playlists.length === 0 ? (
        <div className="text-center py-16">
          <Compass size={48} color="#D35230" weight="duotone" className="mx-auto mb-4" />
          <p className="font-serif-display text-2xl sm:text-3xl text-[#4A5A50] mb-2">No public playlists yet.</p>
          <p className="text-sm text-[#1F2924]/60">Be the first — make one of your playlists public from your Library.</p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-5">
          {playlists.map((pl) => (
            <Link
              key={pl.id}
              to={`/share/playlist/${pl.share_token}`}
              data-testid="discover-playlist-card"
              className="group bg-[#EAE4D3] border-2 border-[#1F2924]/20 hover:border-[#1F2924] p-4 sm:p-5 transition-colors flex flex-col"
            >
              <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#D35230] mb-1 flex items-center gap-1.5">
                <Share size={11} weight="fill" /> Public
              </div>
              <h3 className="font-serif-display text-xl sm:text-2xl leading-tight line-clamp-2 min-h-[3rem]">{pl.name}</h3>
              {pl.description && (
                <p className="text-sm text-[#1F2924]/70 mt-1 line-clamp-2">{pl.description}</p>
              )}
              <div className="mt-auto pt-4 flex items-center justify-between border-t border-[#1F2924]/10">
                <div className="flex items-center gap-2 min-w-0">
                  {pl.owner?.picture ? (
                    <img src={pl.owner.picture} alt="" className="w-7 h-7 border border-[#1F2924]/30" />
                  ) : (
                    <div className="w-7 h-7 bg-[#3C5A41] text-[#F4F1EA] flex items-center justify-center font-mono text-xs">
                      {pl.owner?.name?.[0] || "?"}
                    </div>
                  )}
                  <span className="font-sans text-xs truncate">{pl.owner?.name || "Anonymous"}</span>
                </div>
                <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#4A5A50] flex items-center gap-1.5">
                  <ListBullets size={11} /> {pl.track_count} <ArrowRight size={11} className="opacity-0 group-hover:opacity-100 transition-opacity" />
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
